import { Share, ContentCopy } from "@mui/icons-material"
import { Box, Typography, useMediaQuery } from "@mui/material"
import { IconInfoCircleFilled } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { toast } from "react-toastify"
import { ContractSign } from "~/assets/icons"
import { TermOfAdhesionStatus } from "~/components"
import { useIndicationFlow } from "~/contexts"
import { useGetProposalAndIndicationByIdQuery } from "~/graphql/types"
import { EDocumentSignStatus, ETermOfManagementType, TermOfAdhesionModel } from "~/graphql/types"
import { theme, trimString } from "~/utils"
import { copyToClipboard } from "~/utils/clipboard"

const getCardBorder = (hasSigned: boolean, status?: EDocumentSignStatus) => {

  if (status === EDocumentSignStatus.refused) return theme.palette.error.main

  if (hasSigned) return '#00A86B'

  if (status === EDocumentSignStatus.expired) return '#CDCDCD'

  if (status === EDocumentSignStatus.uploaded) return theme.palette.info.dark

  if (status === EDocumentSignStatus.signatureRequested) return theme.palette.primary.main

  if (status) return '#8989890D'

  return theme.palette.primary.main
}

{/* WARNING - Dentro da proposta o `termOfAdhesion` na verdade está com o nome errado, na verdade é o `termOfManagement` */ }

type ProposalManagementTermCardProps = {
  adhesionTerm?: TermOfAdhesionModel | null
}

export const ClientSignature: React.FC<ProposalManagementTermCardProps> = ({ adhesionTerm }) => {
  const hasSigned = Boolean(adhesionTerm?.signedAt)
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isLowerMd = useMediaQuery(theme.breakpoints.down('md'))
  const isManualManagement = adhesionTerm?.manualOrAutomatic === ETermOfManagementType.manual
  const { selectedIndication, selectedProposal, setSelectedProposal, goToPage, stepper, isPf } = useIndicationFlow()
  const pollingIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const isPollingRef = useRef(false)
  const currentSignerUrl = selectedProposal?.termOfAdhesion?.signerUrl || adhesionTerm?.signerUrl

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = undefined
      isPollingRef.current = false
    }
  }, [])

  const { refetch } = useGetProposalAndIndicationByIdQuery({
    variables: {
      id: selectedIndication?._id || ''
    },
    fetchPolicy: 'network-only',
    skip: false,
    onCompleted(data) {
      if (data.getIndicationRelatedEntitiesById.proposal) {
        setSelectedProposal(data.getIndicationRelatedEntitiesById.proposal)
      }
    }
  })

  const checkForSignerUrl = useCallback(async () => {
    try {
      const result = await refetch()

      if (result.data.getIndicationRelatedEntitiesById.proposal) {
        // Sempre atualiza a proposta para manter o componente sincronizado
        setSelectedProposal(result.data.getIndicationRelatedEntitiesById.proposal)

        const newSignerUrl = result.data.getIndicationRelatedEntitiesById.proposal.termOfAdhesion?.signerUrl

        if (newSignerUrl) {
          stopPolling()
        }
      }
    } catch (error) {

    }
  }, [refetch, setSelectedProposal, stopPolling, goToPage, stepper])  // Effect para gerenciar o polling
  useEffect(() => {
    const startPolling = () => {
      if (isPollingRef.current) {

        return
      }
      isPollingRef.current = true
      checkForSignerUrl() // Primeira verificação imediata

      pollingIntervalRef.current = setInterval(checkForSignerUrl, 5000)
    }

    if (!selectedIndication?._id) {
      return
    }

    if (selectedProposal?.termOfAdhesion?.signerUrl || adhesionTerm?.signerUrl) {
      return
    }

    startPolling()

    return () => {
      stopPolling()
    }
  }, [selectedIndication?._id, checkForSignerUrl, selectedProposal?.termOfAdhesion?.signerUrl, adhesionTerm?.signerUrl, stopPolling])

  // Effect separado para parar o polling quando encontrar signerUrl
  useEffect(() => {
    if (selectedProposal?.termOfAdhesion?.signerUrl || adhesionTerm?.signerUrl) {
      stopPolling()
    }
  }, [selectedProposal?.termOfAdhesion?.signerUrl, adhesionTerm?.signerUrl, stopPolling])


  const signers = useMemo(() => {
    return adhesionTerm?.signers.map(signer => (
      <Box gap={1} display='flex' flexDirection={isLowerMd ? 'column' : 'row'} key={`${signer.email}.${signer.name}`}>
        {/* {!isLowerMd && ( */}
        <Typography display='inline-block'>
          • Assinante:
        </Typography>
        {/* )} */}
        <Typography color='#999999'>
          {signer.name}
        </Typography>
      </Box>
    ))
  }, [adhesionTerm])

  const onCopyToClipboard = (link: string) => {
    copyToClipboard(link)
    toast.success('Link copiado!', {
      position: 'top-right',
      autoClose: 3000
    })
  }
  return (
    <Box>
      {!currentSignerUrl && (
        <Box sx={{
          gap: 3,
          padding: 3,
          display: 'flex',
          borderRadius: '12px',
          alignItems: 'center',
          flexDirection: 'row',
          border: `1px solid ${theme.palette.primary.main}`,
          marginBottom: '1.5rem',
        }}>
          <Box sx={{
            width: '40px',
          }}>
            <IconInfoCircleFilled size={40} color={theme.palette.primary.main} />
          </Box>
          <Typography variant='body1'>
            ATENÇÃO: O termo de gestão está sendo gerado. Em alguns instantes, você terá acesso para prosseguir com a assinatura.
          </Typography>
        </Box>
      )}
      <Typography fontWeight={500} variant='h3'>Termo de gestão</Typography>
      {currentSignerUrl ? (
        <Box sx={{
          gap: 3,
          padding: 3,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'row',
          border: '1px solid #E6E6E6',
          borderLeft: `6px solid ${getCardBorder(hasSigned, adhesionTerm?.status)}`,
          marginTop: '1.5rem'
        }}>

          {!isLowerSm &&
            <ContractSign />
          }

          <Box sx={{
            gap: 3,
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{
                gap: 3,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                flexWrap: 'wrap'
              }}>
                <Typography>Assinatura do cliente</Typography>
                <TermOfAdhesionStatus status={adhesionTerm?.status || EDocumentSignStatus.created} />
                {!isLowerMd && (
                  <Typography color='#999999'>
                    {adhesionTerm?.status === EDocumentSignStatus.expired
                      ? 'Gere um novo termo de gestão'
                      : hasSigned
                        ? 'Assinado pelo cliente'
                        : 'Aguardando cliente assinar'}
                  </Typography>
                )}
              </Box>
              {/* COLOCAR AQUI A OPÇÃO DE GERAR UM NOVO TERMO */}
              {/* {!isManualManagement && (adhesionTerm?.status === EDocumentSignStatus.signed || adhesionTerm?.status === EDocumentSignStatus.expired) && <TermOfManagementOptions term={adhesionTerm} />} */}

            </Box>
            {signers}

            {currentSignerUrl && (
              <Box gap={2} display='flex' flexDirection='column'>
                <Box flexDirection='row' display='flex' alignItems='center' gap={1}>
                  <Typography fontWeight={600}>Compartilhar</Typography>
                  <Share color='action' />
                </Box>

                <Typography variant='body2' color='#999999'>Link para termo de gestão (Copie e cole onde desejar):</Typography>
                <Box
                  onClick={() => adhesionTerm?.status !== EDocumentSignStatus.expired && onCopyToClipboard(currentSignerUrl || '')}
                  sx={{
                    display: 'flex',
                    padding: 2,
                    borderRadius: '8px',
                    border: '1px solid #E6E6E6',
                    justifyContent: 'space-between',
                    cursor: adhesionTerm?.status === EDocumentSignStatus.expired ? 'not-allowed' : 'pointer',
                    userSelect: adhesionTerm?.status === EDocumentSignStatus.expired ? 'none' : 'auto',
                    alignItems: 'center'
                  }}>
                  <Typography sx={{ paddingRight: '1rem' }} variant='body2' color={adhesionTerm?.status === EDocumentSignStatus.expired ? '#CDCDCD' : '#000000DE'}>
                    {trimString(currentSignerUrl, { maxTextLength: isLowerSm ? 20 : 100, textCutLength: isLowerSm ? 20 : 100 })}
                  </Typography>

                  <ContentCopy color={adhesionTerm?.status === EDocumentSignStatus.expired ? 'disabled' : 'action'} fontSize='small' />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <>Gerando o termo...</>
      )}
    </Box>
  )
}